import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { PanelLeft, Plus, Trash2, GripVertical, Type, Heading, Image, Square, Code, Search, ListFilter, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const WIDGET_TYPES = [
  { type: 'heading', label: 'Heading', icon: Heading },
  { type: 'text', label: 'Text Block', icon: Type },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'button', label: 'Button/Link', icon: Square },
  { type: 'html', label: 'Custom HTML', icon: Code },
  { type: 'search', label: 'Search Box', icon: Search },
  { type: 'categories', label: 'Categories', icon: ListFilter },
  { type: 'recent-posts', label: 'Recent Posts', icon: ListFilter },
];

// Preview Widget for sidebar preview
function PreviewWidget({ widget }) {
  switch (widget.type) {
    case 'heading':
      const HeadingTag = widget.content.level || 'h3';
      return <HeadingTag className="text-lg font-semibold text-slate-900 mb-2">{widget.content.text || 'Widget Title'}</HeadingTag>;
    case 'text':
      return <p className="text-sm text-slate-600 mb-4">{widget.content.text || 'Text content...'}</p>;
    case 'button':
      return (
        <span className="inline-block w-full text-center px-4 py-2 rounded-lg font-medium mb-4" 
          style={{ backgroundColor: widget.content.bgColor || '#2563eb', color: widget.content.textColor || '#fff' }}>
          {widget.content.text || 'Click Here'}
        </span>
      );
    case 'search':
      return (
        <div className="mb-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm" placeholder={widget.content.placeholder || 'Search...'} disabled />
        </div>
      );
    case 'recent-posts':
      return (
        <div className="mb-4">
          {widget.content.title && <h4 className="text-base font-semibold mb-3">{widget.content.title}</h4>}
          <ul className="space-y-2 text-sm text-slate-600"><li>Sample Post 1</li><li>Sample Post 2</li></ul>
        </div>
      );
    default:
      return <div className="mb-4 p-2 bg-slate-100 rounded text-sm text-slate-500">{widget.type}</div>;
  }
}

// Sortable Widget Item
function SortableWidget({ widget, onUpdate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const updateContent = (key, value) => {
    onUpdate(widget.id, { ...widget, content: { ...widget.content, [key]: value } });
  };

  const widgetType = WIDGET_TYPES.find(w => w.type === widget.type);
  const Icon = widgetType?.icon || Type;

  const renderEditor = () => {
    switch (widget.type) {
      case 'heading':
        return (
          <div className="space-y-2">
            <Input value={widget.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Widget title..." className="bg-slate-600 border-slate-500 text-white text-sm" />
            <Select value={widget.content.level || 'h3'} onValueChange={(val) => updateContent('level', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
                <SelectItem value="h4">H4</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      case 'text':
        return <Textarea value={widget.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Enter text..." className="bg-slate-600 border-slate-500 text-white text-sm min-h-[60px]" />;
      case 'button':
        return (
          <div className="space-y-2">
            <Input value={widget.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Button text..." className="bg-slate-600 border-slate-500 text-white text-sm" />
            <Input value={widget.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="URL..." className="bg-slate-600 border-slate-500 text-white text-sm" />
            <div className="flex gap-2">
              <div className="flex items-center gap-1"><span className="text-xs text-slate-400">BG:</span><input type="color" value={widget.content.bgColor || '#2563eb'} onChange={(e) => updateContent('bgColor', e.target.value)} className="w-6 h-6 rounded cursor-pointer border-0" /></div>
              <div className="flex items-center gap-1"><span className="text-xs text-slate-400">Text:</span><input type="color" value={widget.content.textColor || '#ffffff'} onChange={(e) => updateContent('textColor', e.target.value)} className="w-6 h-6 rounded cursor-pointer border-0" /></div>
            </div>
          </div>
        );
      case 'search':
        return <Input value={widget.content.placeholder || ''} onChange={(e) => updateContent('placeholder', e.target.value)} placeholder="Search placeholder..." className="bg-slate-600 border-slate-500 text-white text-sm" />;
      case 'recent-posts':
        return (
          <div className="space-y-2">
            <Input value={widget.content.title || ''} onChange={(e) => updateContent('title', e.target.value)} placeholder="Widget title..." className="bg-slate-600 border-slate-500 text-white text-sm" />
            <Select value={widget.content.count || '5'} onValueChange={(val) => updateContent('count', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="3">3 posts</SelectItem>
                <SelectItem value="5">5 posts</SelectItem>
                <SelectItem value="10">10 posts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      case 'html':
        return <Textarea value={widget.content.html || ''} onChange={(e) => updateContent('html', e.target.value)} placeholder="<div>HTML...</div>" className="bg-slate-600 border-slate-500 text-white text-sm min-h-[60px] font-mono" />;
      case 'image':
        return (
          <div className="space-y-2">
            <Input value={widget.content.src || ''} onChange={(e) => updateContent('src', e.target.value)} placeholder="Image URL..." className="bg-slate-600 border-slate-500 text-white text-sm" />
            <Input value={widget.content.alt || ''} onChange={(e) => updateContent('alt', e.target.value)} placeholder="Alt text..." className="bg-slate-600 border-slate-500 text-white text-sm" />
          </div>
        );
      case 'categories':
        return <Input value={widget.content.title || ''} onChange={(e) => updateContent('title', e.target.value)} placeholder="Widget title..." className="bg-slate-600 border-slate-500 text-white text-sm" />;
      default:
        return null;
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-slate-700 rounded p-2 mb-2">
      <div className="flex items-start gap-2">
        <div {...attributes} {...listeners} className="p-1 cursor-grab text-slate-400 hover:text-white"><GripVertical className="w-4 h-4" /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Icon className="w-3 h-3 text-blue-400" />
            <span className="text-xs text-slate-400">{widgetType?.label || widget.type}</span>
          </div>
          {renderEditor()}
        </div>
        <button onClick={() => onDelete(widget.id)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

export default function PageSidebarSettings({ sidebarSettings, onChange }) {
  const [showPreview, setShowPreview] = useState(false);
  
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleModeChange = (mode) => {
    onChange({ ...sidebarSettings, mode, widgets: mode === 'custom' ? (sidebarSettings.widgets || []) : [] });
  };

  const addWidget = (type) => {
    const newWidget = {
      id: `widget_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      content: type === 'heading' ? { text: 'Widget Title', level: 'h3' } :
               type === 'text' ? { text: '' } :
               type === 'image' ? { src: '', alt: '' } :
               type === 'button' ? { text: 'Click Here', url: '#', bgColor: '#2563eb', textColor: '#ffffff' } :
               type === 'html' ? { html: '' } :
               type === 'search' ? { placeholder: 'Search...' } :
               type === 'categories' ? { title: 'Categories' } :
               type === 'recent-posts' ? { title: 'Recent Posts', count: '5' } : {}
    };
    onChange({ ...sidebarSettings, widgets: [...(sidebarSettings.widgets || []), newWidget] });
  };

  const updateWidget = (id, updatedWidget) => {
    onChange({ ...sidebarSettings, widgets: sidebarSettings.widgets.map(w => w.id === id ? updatedWidget : w) });
  };

  const deleteWidget = (id) => {
    onChange({ ...sidebarSettings, widgets: sidebarSettings.widgets.filter(w => w.id !== id) });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = sidebarSettings.widgets.findIndex(w => w.id === active.id);
      const newIndex = sidebarSettings.widgets.findIndex(w => w.id === over.id);
      onChange({ ...sidebarSettings, widgets: arrayMove(sidebarSettings.widgets, oldIndex, newIndex) });
    }
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <PanelLeft className="w-4 h-4 text-blue-400" />
          Sidebar Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode Selection */}
        <div>
          <label className="text-sm text-slate-400 block mb-2">Sidebar Mode</label>
          <Select value={sidebarSettings.mode || 'global'} onValueChange={handleModeChange}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="sidebar-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="global">Use Global Sidebar</SelectItem>
              <SelectItem value="custom">Custom Sidebar</SelectItem>
              <SelectItem value="none">No Sidebar</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Position/Width overrides for global mode */}
        {sidebarSettings.mode === 'global' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 block mb-1">Position Override</label>
              <Select value={sidebarSettings.position || 'default'} onValueChange={(val) => onChange({ ...sidebarSettings, position: val === 'default' ? null : val })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Width Override</label>
              <Select value={sidebarSettings.width || 'default'} onValueChange={(val) => onChange({ ...sidebarSettings, width: val === 'default' ? null : val })}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm">
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Custom Sidebar Builder */}
        {sidebarSettings.mode === 'custom' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Position</label>
                <Select value={sidebarSettings.position || 'right'} onValueChange={(val) => onChange({ ...sidebarSettings, position: val })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Width</label>
                <Select value={sidebarSettings.width || 'md'} onValueChange={(val) => onChange({ ...sidebarSettings, width: val })}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="sm">Small</SelectItem>
                    <SelectItem value="md">Medium</SelectItem>
                    <SelectItem value="lg">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add Widgets */}
            <div>
              <label className="text-xs text-slate-400 block mb-2">Add Widgets</label>
              <div className="grid grid-cols-4 gap-1">
                {WIDGET_TYPES.map((widget) => (
                  <button
                    key={widget.type}
                    onClick={() => addWidget(widget.type)}
                    className="flex flex-col items-center gap-1 p-2 bg-slate-700 hover:bg-slate-600 rounded text-center transition-colors"
                    data-testid={`add-widget-${widget.type}`}
                  >
                    <widget.icon className="w-4 h-4 text-blue-400" />
                    <span className="text-[10px] text-slate-300">{widget.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Widgets List */}
            {sidebarSettings.widgets && sidebarSettings.widgets.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-slate-400">Widgets ({sidebarSettings.widgets.length})</label>
                  <Dialog open={showPreview} onOpenChange={setShowPreview}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-xs text-slate-400 hover:text-white h-6 px-2">
                        <Eye className="w-3 h-3 mr-1" /> Preview
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-100 max-w-sm">
                      <DialogHeader><DialogTitle className="text-slate-900">Sidebar Preview</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        {sidebarSettings.widgets.map((widget) => (
                          <div key={widget.id} className="bg-white rounded-lg p-3 shadow-sm border border-slate-200">
                            <PreviewWidget widget={widget} />
                          </div>
                        ))}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={sidebarSettings.widgets.map(w => w.id)} strategy={verticalListSortingStrategy}>
                    <div className="max-h-64 overflow-y-auto">
                      {sidebarSettings.widgets.map((widget) => (
                        <SortableWidget key={widget.id} widget={widget} onUpdate={updateWidget} onDelete={deleteWidget} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
